import { zodResolver } from "@hookform/resolvers/zod"
import { useBlocker } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"

import { type AccountingSettingsFormValues, accountingSettingsFormSchema } from "../schemas/accountingSettingsForm"
import { toAccountingSettingsFormValues, toAccountingSettingsUpdateInput } from "../utils"

import { useUpdateAccountingSettings } from "./useUpdateAccountingSettings"

import type { AccountingSettings } from "@/graphql/codegen/graphql"

interface UseAccountingSettingsFormParams {
  settings: AccountingSettings | undefined
}

interface UseAccountingSettingsFormReturn {
  methods: UseFormReturn<AccountingSettingsFormValues>
  isConfirmationOpen: boolean
  isNavigationBlocked: boolean
  isDirty: boolean
  isUpdating: boolean
  canSubmit: boolean
  handleSaveClick: () => void
  handleConfirmSave: () => Promise<void>
  handleConfirmCancel: () => void
  handleNavigationCancel: () => void
  handleNavigationConfirm: () => void
}

export const useAccountingSettingsForm = ({
  settings,
}: UseAccountingSettingsFormParams): UseAccountingSettingsFormReturn => {
  const methods = useForm<AccountingSettingsFormValues>({
    defaultValues: toAccountingSettingsFormValues(settings),
    resolver: zodResolver(accountingSettingsFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const { updateAccountingSettings, isUpdating } = useUpdateAccountingSettings()

  const blocker = useBlocker({
    shouldBlockFn: () => isDirty && !isUpdating,
    withResolver: true,
    enableBeforeUnload: () => isDirty && !isUpdating,
  })

  useEffect(() => {
    methods.reset(toAccountingSettingsFormValues(settings))
  }, [settings, methods])

  const handleSaveClick = () => {
    void methods.trigger().then((isFormValid) => {
      if (isFormValid) {
        setIsConfirmationOpen(true)
      }
    })
  }

  const handleConfirmSave = methods.handleSubmit(async (formValues: AccountingSettingsFormValues) => {
    const isSuccess = await updateAccountingSettings(toAccountingSettingsUpdateInput(formValues))
    if (isSuccess) {
      setIsConfirmationOpen(false)
      methods.reset(formValues)
    }
  })

  const handleConfirmCancel = () => {
    setIsConfirmationOpen(false)
  }

  const handleNavigationCancel = () => {
    blocker.reset?.()
  }

  const handleNavigationConfirm = () => {
    blocker.proceed?.()
  }

  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting && !isUpdating

  return {
    methods,
    isConfirmationOpen,
    isNavigationBlocked: blocker.status === "blocked",
    isDirty,
    isUpdating,
    canSubmit,
    handleSaveClick,
    handleConfirmSave,
    handleConfirmCancel,
    handleNavigationCancel,
    handleNavigationConfirm,
  }
}
